import JsonLd from '@/components/base/JsonLd';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function PricingGuide() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://allchinabuyspreadsheet.wiki';
  const pagePath = '/allchinabuy-sheet-pricing';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'AllChinaBuySpreadsheet Pricing Strategy: How to Find the Best China Product Deals',
    description: 'Master the AllChinaBuySpreadsheet pricing methodology to find genuine deals on Chinese products. Learn how spreadsheet comparison reveals hidden savings, bulk pricing tactics, and seasonal trend analysis.',
    author: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
    publisher: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet', url: siteUrl },
    datePublished: '2026-06-25',
    dateModified: '2026-06-25',
    mainEntityOfPage: { '@type': 'WebPage', '@id': siteUrl + pagePath },
    url: siteUrl + pagePath,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'AllChinaBuySpreadsheet Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Pricing Strategy', item: siteUrl + pagePath },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <header className="relative h-[420px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Colorful%20price%20tags%20and%20discount%20labels%20floating%20in%20air%2C%20bright%20retail%20store%20background%2C%20shopping%20deals%20concept%2C%20vibrant%20commercial%20photography%2C%20dynamic%20composition%2C%20modern%20consumer%20aesthetic%2C%20energetic%20atmosphere&width=1800&height=840&seq=pricing-hero-v3&orientation=landscape"
          alt="AllChinaBuySpreadsheet pricing guide"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full px-6 md:px-10 pt-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-white/15 backdrop-blur-sm text-xs font-medium text-white rounded-full uppercase tracking-[0.15em] mb-5">Pricing</span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">AllChinaBuySpreadsheet Pricing Strategy: How to Find the Best China Product Deals</h1>
          </div>
        </div>
      </header>
      <article className="py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg text-foreground-700 leading-relaxed mb-8">
            Finding genuinely good deals on Chinese products is not just about picking the lowest price — it is about
            understanding the complete cost picture. The <strong className="text-primary-600 font-semibold">AllChinaBuySpreadsheet</strong>
            pricing methodology goes beyond surface-level price tags to reveal true value across shipping, quality tiers,
            and bulk discounts.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">How Spreadsheet Comparison Reveals Hidden Savings</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            The magic of the <strong className="text-primary-600 font-semibold">china product spreadsheet list</strong> format
            is side-by-side price comparison. When you see 20 similar products with their prices, shipping costs, estimated
            delivery times, and supplier ratings all in one view, patterns emerge immediately. A product priced 30% lower
            than average but with 45-day shipping is not actually a deal — and this becomes obvious when data is organized
            in spreadsheet form. Our system highlights these discrepancies automatically.
          </p>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Among the <strong className="text-primary-600 font-semibold">best china spreadsheet products</strong>, the ones
            offering genuine value typically fall within a specific price-to-quality ratio that our algorithm identifies
            and flags with a "Best Value" badge. These products have been verified to offer competitive pricing without
            sacrificing quality or reliable shipping timelines.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Bulk Pricing and Seasonal Trends</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Another advantage of using <strong className="text-primary-600 font-semibold">allchinabuyspreadsheet</strong> for
            pricing research is access to historical data. Our spreadsheets maintain price histories that reveal genuine
            seasonal discounts versus fake markdowns. A "50% off" tag means nothing if the base price was doubled last week —
            and our system catches these manipulations automatically.
          </p>
          <div className="mt-14 p-8 bg-primary-50 rounded-2xl text-center">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">Start Comparing Prices Now</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">
                Visit KASpreadsheet <i className="ri-arrow-right-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 text-sm font-medium text-foreground-700 rounded-full hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer">
                Back to Home <i className="ri-home-4-line"></i>
              </Link>
            </div>
          </div>
          <nav className="mt-12 pt-8 border-t border-background-200/70">
            <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-4">Related Articles</p>
            <ul className="space-y-2">
              <li><Link to="/allchinabuy-sheet-vs-competitors" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Competitor Comparison: Full Breakdown &rarr;</Link></li>
              <li><Link to="/allchinabuy-sheet-wholesale" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Wholesale Buying: Bulk Orders Made Easy &rarr;</Link></li>
              <li><Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer">&larr; AllChinaBuySpreadsheet Home</Link></li>
            </ul>
          </nav>
        </div>
      </article>
      <Footer />
    </div>
  );
}